import { Component, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { AchievementsComponent } from '../../sidebar/achievements/achievements.component';
import { EonUiButton } from '@eon-ui/eon-ui-components-angular';
import { UserService } from '../../../service/user.service';
import { LevelStartDialogComponent } from '../level-start-dialog/level-start-dialog.component';
import { TranslatePipe } from '@ngx-translate/core';
import { GameThemeService } from '../../../service/game-theme.service';
import { Game } from '../../../enum/game.enum';

export interface LevelSuccessDialogData {
  badgeName: string[] | null;
  currentLevel: number | null;
  nextLevel: number | null;
  userLevelDuration: number | null;
  userLevelAttempts: number | null;
  userTotalScore: { score: string; total_attempts: number; total_pings: number } | null;
}

@Component({
  selector: 'gandalf-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, AchievementsComponent, EonUiButton, TranslatePipe],
  templateUrl: './level-success-dialog.component.html',
  styleUrl: './level-success-dialog.component.scss',
})
export class LevelSuccessDialogComponent {
  protected readonly userService = inject(UserService);
  private readonly dialogRef = inject<MatDialogRef<LevelSuccessDialogComponent>>(MatDialogRef);
  private readonly data = inject<LevelSuccessDialogData>(MAT_DIALOG_DATA);
  private readonly gameThemeService = inject(GameThemeService);
  protected readonly eonUiScheme = this.gameThemeService.getEonUiScheme();
  protected readonly showMoreInfo = this.gameThemeService.getCurrentGame() === Game.GANDALF;
  protected readonly nextLevelButtonLevel = signal(this.getNextLevel());
  protected readonly isStartingNextLevel = signal(false);
  private dialog = inject(MatDialog);

  async startNextLevel(): Promise<void> {
    if (this.isStartingNextLevel()) {
      return;
    }

    const nextLevel = this.getNextLevel();
    this.nextLevelButtonLevel.set(nextLevel);
    this.isStartingNextLevel.set(true);

    this.userService.clearChatHistory();
    await this.userService.setLevel(nextLevel);
    this.userService.loadUserDataWithoutHistory();

    // Close the dialog and wait for it to fully close before opening the next one
    // This prevents race conditions and ensures the dialog stack is clean
    this.dialogRef.close(true);

    setTimeout(() => {
      // Pass nextLevel to LevelStartDialog via data to prevent race conditions
      // The dialog will use this level instead of fetching it from backend
      this.dialog.open(LevelStartDialogComponent, {
        width: '80vw',
        maxWidth: '80vw',
        disableClose: true,
        panelClass: 'animateIn',
        backdropClass: 'backdropAnimateIn',
        data: { level: nextLevel },
      });
    }, 500);
  }

  private getNextLevel(): number {
    return this.data.nextLevel ?? (this.userService.userLevel() ?? 1) + 1;
  }
}
